import { LANGUAGES, STAGE_CHANNELS, STAGE_TYPES, channelLabel } from "../campaignUtils";

export const CampaignStep = {
  Audience: "audience",
  Content: "content",
  Analytics: "analytics",
  Test: "test",
  Approve: "approve",
  Deliver: "deliver"
};

export const AUTOMATED_CAMPAIGN_STEPS = [
  { id: CampaignStep.Audience, label: "Customer groups" },
  { id: CampaignStep.Content, label: "Notification content" },
  { id: CampaignStep.Test, label: "Test notification" },
  { id: CampaignStep.Analytics, label: "Results" }
];

export const hasCompleteContent = payload => {
  const settings = payload && payload.settings;
  if (payload && payload.error && payload.generation_id) return false;
  if (!settings || !Array.isArray(settings.channels) || !settings.channels.length ||
      !Array.isArray(payload.content) || payload.content.length !== 4) return false;
  const languages = getSelectedLanguages(settings);
  if (!languages.length) return false;
  if (settings.channels.includes("email") && (!Array.isArray(payload.banners) || !payload.banners[2])) return false;
  return payload.content.every((stage, index) =>
    stage && stage.type === STAGE_TYPES[index].toLowerCase() &&
    [STAGE_CHANNELS[index]].every(channel => languages.every(language => {
      const variant = (((stage.variants || {})[channel] || {})[language]);
      return variant && !!variant.body && (channel === "sms" || !!variant.subject);
    }))
  );
};

export const getSelectedLanguages = settings => {
  const selected = settings.language_mode === "preferred" ? LANGUAGES : settings.languages || [];
  return selected.filter(language => LANGUAGES.includes(language));
};

export const allChannelsTested = (settings, tested) => {
  const channels = (settings && settings.channels) || [];
  return channels.length > 0 && channels.every(channel => tested.includes(channel));
};

// The step the campaign is actually at, derived from shared server state plus
// local facts: audience reviewed, unsaved edits, "content reviewed" and
// "testing done or skipped" for this revision.
export const getCurrentCampaignStep = ({ campaign, audienceConfirmed, contentConfirmedRevision }) => {
  const payload = campaign.payload;
  if (["audience_building", "audience_failed"].includes(campaign.status) || !payload.audience_ready) {
    return CampaignStep.Audience;
  }
  // Opening a campaign always starts on the audience; the admin continues from there.
  if (!audienceConfirmed) return CampaignStep.Audience;
  if (campaign.status === "generating") return CampaignStep.Content;
  if (!hasCompleteContent(payload)) return CampaignStep.Content;
  return contentConfirmedRevision === payload.revision ? CampaignStep.Test : CampaignStep.Content;
};

// Steps shown on the page: every finished step (collapsed) plus the current one.
export const getVisibleSteps = currentStep =>
  AUTOMATED_CAMPAIGN_STEPS.slice(0, AUTOMATED_CAMPAIGN_STEPS.findIndex(step => step.id === currentStep) + 1);

// One-line summary shown on a collapsed, finished step.
export const getStepSummary = (stepId, { campaign, tested }) => {
  const payload = campaign.payload;
  const settings = payload.settings;
  switch (stepId) {
    case CampaignStep.Audience:
      return "Eligible customers · selected notification day";
    case CampaignStep.Content:
      return settings
        ? `${settings.channels.map(channelLabel).join(", ")} · ${getSelectedLanguages(settings).join(", ")} · Revision ${payload.revision}`
        : `Revision ${payload.revision}`;
    case CampaignStep.Test:
      return tested.length
        ? `Tested: ${tested.map(channelLabel).join(", ")}`
        : "Skipped";
    case CampaignStep.Approve:
      return payload.approval ? `Revision ${payload.approval.revision} approved by admin ${payload.approval.admin_id}` : "Approved";
    default:
      return "";
  }
};

export const getStepInstruction = ({ campaign, currentStep }) => {
  switch (currentStep) {
    case CampaignStep.Audience:
      if (campaign.status === "audience_failed") return "Retry customer selection";
      return campaign.payload.audience_ready ? "Review eligible customers" : "Preparing eligible customers";
    case CampaignStep.Content:
      if (campaign.status === "generating") return "AI is writing content and banners";
      if (campaign.payload.error && campaign.payload.generation_id) return "Retry the failed step to continue generation";
      if (!hasCompleteContent(campaign.payload)) return "Waiting for personalized notification content";
      return "Review the personalized notifications";
    case CampaignStep.Test:
      return "Send an optional test; customer notifications remain automatic";
    default:
      return "";
  }
};

// Single primary action for the current step, like the pickup-order flow.
export const getCampaignAction = ({ currentStep, derived, handlers }) => {
  const { campaign, stale, canUseSaved } = derived;
  const payload = campaign.payload;

  switch (currentStep) {
    case CampaignStep.Audience:
      if (campaign.status === "audience_failed") {
        return { label: "Retry customer selection", pendingLabel: "Retrying…", onClick: handlers.onRetryAudience };
      }
      return payload.audience_ready
        ? { label: "Next", onClick: handlers.onConfirmAudience }
        : null;
    case CampaignStep.Content:
      if (campaign.status === "generating" || stale) return null;
      if (payload.error && payload.generation_id) {
        return {
          label: "Retry failed step and continue",
          pendingLabel: "Resuming…",
          disabled: !derived.canRetryGeneration,
          hint: derived.canRetryGeneration ? undefined : "Discard unsaved edits before retrying.",
          onClick: handlers.onRetryGeneration
        };
      }
      if (!hasCompleteContent(payload)) {
        return null;
      }
      return { label: "Next", disabled: !canUseSaved, onClick: handlers.onConfirmContent };
    case CampaignStep.Test:
      return null;
    default:
      return null;
  }
};
